import { useNavigate } from "react-router-dom";
/**
 * @file App bar user menu: read-only account details from the access token, logout.
 */
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useAuth } from "@/lib/auth-context";
import { displayNameInitials, resolveAvatarSrc } from "@/lib/avatar";
import { useMemo, useState } from "react";

/**
 * Profile icon, display name or email, and account dropdown on the app bar.
 */
export function DashboardAccountMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [accountOpen, setAccountOpen] = useState(false);

  const barAvatarSrc = useMemo(() => resolveAvatarSrc(user?.picture), [user?.picture]);

  const barInitials = useMemo(
    () => displayNameInitials(user?.name, user?.email || user?.sub || ""),
    [user?.email, user?.name, user?.sub]
  );

  if (!user) {
    return null;
  }

  const showName = Boolean(user.name?.trim());

  return (
    <>
      <Button
        color="inherit"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          textTransform: "none",
          color: "text.primary",
          maxWidth: { xs: 56, sm: 280 },
          minWidth: 0,
          px: { xs: 0.5, sm: 1 },
        }}
        aria-haspopup="menu"
        aria-expanded={menuOpen ? "true" : undefined}
        aria-label="Account menu"
        startIcon={
          <Avatar
            key={user.sub + (user.picture ?? "")}
            src={barAvatarSrc}
            imgProps={{ alt: "" }}
            sx={{
              width: 34,
              height: 34,
              fontSize: "0.85rem",
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            {barInitials}
          </Avatar>
        }
        endIcon={<ArrowDropDownIcon sx={{ display: { xs: "none", sm: "inline-flex" } }} />}
      >
        {showName ? (
          <Typography
            component="span"
            variant="body2"
            fontWeight={600}
            noWrap
            sx={{ display: { xs: "none", sm: "inline" }, maxWidth: 160, textAlign: "left" }}
          >
            {user.name}
          </Typography>
        ) : (
          <Typography
            component="span"
            variant="body2"
            sx={{ display: { xs: "none", sm: "inline" }, maxWidth: 180 }}
            noWrap
          >
            {user.email?.trim() || user.sub}
          </Typography>
        )}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setAccountOpen(true);
          }}
        >
          <ListItemIcon>
            <PersonOutlineIcon fontSize="small" />
          </ListItemIcon>
          Account
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            const fullNavigation = logout();
            if (!fullNavigation) {
              navigate("/login");
            }
          }}
        >
          <ListItemIcon>
            <LogoutOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <Dialog open={accountOpen} onClose={() => setAccountOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <AccountCircleOutlinedIcon color="primary" />
            Account
          </Box>
          <IconButton
            onClick={() => setAccountOpen(false)}
            aria-label="Close account dialog"
            edge="end"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Avatar
              src={barAvatarSrc}
              imgProps={{ alt: "" }}
              sx={{ width: 80, height: 80, fontSize: "1.75rem", bgcolor: "primary.main" }}
            >
              {barInitials}
            </Avatar>
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Name and email come from your Auth0 profile. Roles for this app come from your API access token.
          </Typography>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <div>
              <Typography variant="caption" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1">{user.email?.trim() || "—"}</Typography>
            </div>
            {user.name?.trim() ? (
              <div>
                <Typography variant="caption" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1">{user.name}</Typography>
              </div>
            ) : null}
            <div>
              <Typography variant="caption" color="text.secondary">
                ID
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                {user.sub}
              </Typography>
            </div>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
